//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  CampaignSubTypeFilter,
  CampaignTypeFilter,
  IContentLibraryFiltersOptions,
  MediaFilterParamKey,
  RIGHTS_STATUS,
} from '@apis/content-library/contentLibraryApiModel';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { isDefined } from '@utils/filterPredicates';

const sectionToSelectedValues = (section: FilterSection<FilterValue>) => {
  return section.options
    .filter((option) => option.isSelected)
    .map((option) => option.value);
};

export const sectionToSelectedStringValues = <T extends string>(
  section: FilterSection<FilterValue>,
) => {
  return sectionToSelectedValues(section).map((option) => option as T);
};

export const sectionToSelectedValue = <T extends string | boolean>(
  section: FilterSection<FilterValue>,
): T | undefined => {
  const selectedOption = section.options.find((option) => option.isSelected);

  if (selectedOption) {
    return selectedOption.value as T;
  }
};

export interface ContentItemsQueryParams {
  filters?: IContentLibraryFiltersOptions;
  sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>;
  skipLabels?: boolean;
}

export const useContentLibraryParamsMapper = ({
  filters = { labels: [] },
  sections,
  skipLabels = false,
}: ContentItemsQueryParams) => {
  let filtersCount = 0;

  // PD-301894: Remove excludeTargetedRetailerCampaigns and excludeNonTargetedRetailerCampaigns
  sections.forEach((section, paramKey) => {
    switch (paramKey) {
      case 'dateRange':
        filters[paramKey] = sectionToSelectedValue(section);
        filtersCount += isDefined(filters[paramKey]) ? 1 : 0;
        break;
      case 'excludeNonTargetedRetailerCampaignItems':
      case 'excludeTargetedRetailerCampaignItems':
      case 'hasLabels':
      case 'hasProducts':
      case 'spamFiltering':
        filters[paramKey] = sectionToSelectedValue(section);
        filtersCount += isDefined(filters[paramKey]) ? 1 : 0;
        break;
      case 'labels':
        if (!skipLabels) {
          filters[paramKey] = sectionToSelectedStringValues(section);
          filtersCount += filters[paramKey].length;
        }
        break;
      case 'igHashtags':
      case 'igUsernames':
      case 'mediaItemTypes':
      case 'orientation':
      case 'productIds':
        filters[paramKey] = sectionToSelectedStringValues(section);
        filtersCount += filters[paramKey].length;
        break;
      case 'userIds':
        filters[paramKey] = sectionToSelectedStringValues(section);
        break;
      case 'campaignSubTypes':
        filters[paramKey] =
          sectionToSelectedStringValues<CampaignSubTypeFilter>(section);
        filtersCount += filters[paramKey].length;
        break;
      case 'campaignTypes':
        filters[paramKey] =
          sectionToSelectedStringValues<CampaignTypeFilter>(section);
        filtersCount += filters[paramKey].length;
        break;
      case 'rightsStatuses':
        filters[paramKey] =
          sectionToSelectedStringValues<RIGHTS_STATUS>(section);
        filtersCount += filters[paramKey].length;
        break;
      case 'isDistributed': {
        const distribution = sections.get('distribution');

        // Skip isDistributed if distribution is undefined or empty
        if (
          isDefined(distribution) &&
          sectionToSelectedStringValues(distribution).length > 0
        ) {
          filters.isDistributed = sectionToSelectedValue<boolean>(section);
        }
        break;
      }
      case 'distribution': {
        const isDistributed = sections.get('isDistributed');

        // Skip distribution if isDistributed is undefined
        if (isDefined(isDistributed)) {
          filters.distribution = sectionToSelectedStringValues(section);
          filtersCount += filters.distribution.length;
        }
        break;
      }
      case 'semanticTags':
        filters[paramKey] = sectionToSelectedStringValues(section).map(
          (tag) => ({ tag }),
        );
        filtersCount += filters[paramKey].length;
        break;
      // TODO: not implemented yet
      case 'createdAfter':
      case 'filter':
      case 'isScheduled':
      case 'itemTypes':
      case 'itemId':
      case 'searchText':
    }
  });
  return { filters, filtersCount };
};
